import React, { useEffect, useState } from "react";
import api, { apiErr, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, todayISO, formatDate } from "@/lib/format";
import { F } from "@/pages/Employees";
import { VehicleSelect, FotoField, canEditRow } from "@/components/vehicle/VehicleServices";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Fuel, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const BBM = ["Pertalite", "Pertamax", "Solar / Biosolar", "Dexlite", "Pertamina Dex"];

export default function VehicleFuel({ vehicles }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [vid, setVid] = useState("all");
  const [form, setForm] = useState(null);
  const [foto, setFoto] = useState(null);
  const load = () => api.get(`/vehicle-fuel${vid !== "all" ? `?vehicle_id=${vid}` : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vid]);
  const set = (k, val) => setForm((f) => {
    const n = { ...f, [k]: val };
    if (k === "liter" || k === "harga_per_liter") n.total = Math.round(Number(n.liter || 0) * Number(n.harga_per_liter || 0)) || n.total;
    return n;
  });
  const save = async () => {
    if (!form.vehicle_id) return toast.error("Pilih kendaraan (plat nomor)");
    if (!Number(form.total)) return toast.error("Isi nominal BBM");
    try {
      if (form.id) await api.put(`/vehicle-fuel/${form.id}`, form); else await api.post("/vehicle-fuel", form);
      toast.success("Pengisian BBM tersimpan"); setForm(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Hapus BBM ${r.plat_no} tanggal ${formatDate(r.tanggal)}?`)) return;
    try { await api.delete(`/vehicle-fuel/${r.id}`); toast.success("Data BBM dihapus"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const v = vehicles.find((x) => x.id === form?.vehicle_id);
  return (
    <Card className="p-0 overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 p-4 border-b">
        <div className="w-full sm:w-64"><VehicleSelect vehicles={vehicles} value={vid} onChange={setVid} testid="bbm-filter-vehicle" all /></div>
        <div className="text-sm text-slate-500 flex-1">Total: <b data-testid="bbm-grand-total">{rupiah(rows.reduce((s, r) => s + Number(r.total || 0), 0))}</b> · {rows.reduce((s, r) => s + Number(r.liter || 0), 0).toLocaleString("id-ID")} liter</div>
        <Button className="bg-orange-600 hover:bg-orange-700" data-testid="bbm-add-btn"
          onClick={() => setForm({ tanggal: todayISO(), vehicle_id: vid !== "all" ? vid : "", jenis_bbm: "", liter: "", harga_per_liter: "", total: "", km: "", spbu: "", keterangan: "", foto_path: "" })}>
          <Fuel className="h-4 w-4 mr-1" /> Isi BBM</Button>
      </div>
      <div className="overflow-x-auto scroll-thin">
        <table className="w-full text-sm">
          <thead><tr className="bg-blue-900 text-white text-xs uppercase">
            {["Tanggal", "Plat No", "Jenis Kendaraan", "BBM", "Liter", "KM", "SPBU", "Total", "Nota", "Diinput", ""].map((h) => <th key={h} className="px-3 py-2.5 text-left">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={11} className="py-10 text-center text-slate-400">Belum ada pengisian BBM.</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b border-slate-100" data-testid={`bbm-row-${i}`}>
                <td className="px-3 py-2 font-mono whitespace-nowrap">{formatDate(r.tanggal)}</td><td className="px-3 py-2 font-mono font-bold whitespace-nowrap">{r.plat_no}</td>
                <td className="px-3 py-2">{r.jenis}</td><td className="px-3 py-2">{r.jenis_bbm || "-"}</td><td className="px-3 py-2 font-mono">{r.liter || "-"}</td>
                <td className="px-3 py-2 font-mono">{Number(r.km || 0).toLocaleString("id-ID")}</td><td className="px-3 py-2">{r.spbu || "-"}</td>
                <td className="px-3 py-2 font-mono font-semibold whitespace-nowrap">{rupiah(r.total)}</td>
                <td className="px-3 py-2">{r.foto_path ? <button className="text-blue-700" onClick={() => setFoto(fileUrl(r.foto_path))} data-testid={`bbm-foto-${i}`}><ImageIcon className="h-4 w-4" /></button> : <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2 text-xs">{r.mandor_nama || r.created_by}</td>
                <td className="px-3 py-2 whitespace-nowrap">{canEditRow(user, r) && <>
                  <Button size="icon" variant="ghost" data-testid={`bbm-edit-${i}`} onClick={() => setForm({ ...r })}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-red-600" data-testid={`bbm-del-${i}`} onClick={() => del(r)}><Trash2 className="h-4 w-4" /></Button></>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!form} onOpenChange={(o) => { if (!o) setForm(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" data-testid="bbm-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Ubah" : "Catat"} Pengisian BBM</DialogTitle></DialogHeader>
          {form && <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><F label="Kendaraan (Plat No.) *"><VehicleSelect vehicles={vehicles.filter((x) => x.aktif !== false || x.id === form.vehicle_id)} value={form.vehicle_id} onChange={(x) => set("vehicle_id", x)} testid="bbm-f-vehicle" /></F></div>
            {v && <div className="col-span-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900" data-testid="bbm-vehicle-info">Plat <b>{v.plat_no}</b> · Jenis <b>{v.jenis}</b>{v.merk ? ` · ${v.merk}` : ""}</div>}
            <F label="Tanggal"><Input type="date" value={form.tanggal} data-testid="bbm-f-tanggal" onChange={(e) => set("tanggal", e.target.value)} /></F>
            <F label="Jenis BBM">
              <Input list="bbm-list" value={form.jenis_bbm} placeholder="Solar / Pertalite" data-testid="bbm-f-jenis" onChange={(e) => set("jenis_bbm", e.target.value)} />
              <datalist id="bbm-list">{BBM.map((b) => <option key={b} value={b} />)}</datalist>
            </F>
            <F label="Liter"><Input type="number" value={form.liter} data-testid="bbm-f-liter" onChange={(e) => set("liter", e.target.value)} /></F>
            <F label="Harga / Liter"><Input type="number" value={form.harga_per_liter} data-testid="bbm-f-harga" onChange={(e) => set("harga_per_liter", e.target.value)} /></F>
            <F label="Total Nominal (Rp) *"><Input type="number" value={form.total} data-testid="bbm-f-total" onChange={(e) => set("total", e.target.value)} /></F>
            <F label="KM / Odometer"><Input type="number" value={form.km} data-testid="bbm-f-km" onChange={(e) => set("km", e.target.value)} /></F>
            <div className="col-span-2"><F label="SPBU / Lokasi"><Input value={form.spbu} data-testid="bbm-f-spbu" onChange={(e) => set("spbu", e.target.value)} /></F></div>
            <div className="col-span-2"><F label="Foto Nota / Struk"><FotoField value={form.foto_path} onChange={(p) => set("foto_path", p)} testid="bbm-f-foto" /></F></div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Batal</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={save} data-testid="bbm-save-btn"><Plus className="h-4 w-4 mr-1" /> Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!foto} onOpenChange={(o) => { if (!o) setFoto(null); }}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Foto Nota BBM</DialogTitle></DialogHeader>{foto && <img src={foto} alt="nota" className="w-full rounded" />}</DialogContent>
      </Dialog>
    </Card>
  );
}
